import { useState, useEffect } from 'react'
import { Avatar, CircularProgress, Box } from '@mui/material'
import ImageIcon from '@mui/icons-material/Image'

const CachedImage = ({ 
  src, 
  alt = '', 
  size = 64, 
  variant = 'rounded',
  fallbackIcon = true,
  ...props 
}) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [cachedSrc, setCachedSrc] = useState(null)

  useEffect(() => {
    if (!src) {
      setImageLoaded(false)
      setImageError(true)
      return
    }

    // Reset states when src changes
    setImageLoaded(false)
    setImageError(false)
    setCachedSrc(null)

    // Create new image element for preloading
    const img = new Image()
    
    img.onload = () => {
      setCachedSrc(src)
      setImageLoaded(true)
      setImageError(false)
    }
    
    img.onerror = () => {
      setImageLoaded(false)
      setImageError(true)
      setCachedSrc(null)
    }
    
    // Start loading
    img.src = src
    
    // Cleanup function
    return () => {
      img.onload = null
      img.onerror = null
    }
  }, [src])

  const avatarSize = typeof size === 'number' ? { width: size, height: size } : size

  // Show loading state
  if (!imageLoaded && !imageError && src) {
    return (
      <Box
        sx={{
          ...avatarSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          borderRadius: variant === 'rounded' ? 1 : '50%',
          border: '1px solid #e0e0e0',
          ...props.sx
        }}
      >
        <CircularProgress size={size * 0.3} sx={{ color: '#999' }} />
      </Box>
    )
  }

  // Show error state or no image
  if (imageError || !src) {
    return (
      <Avatar
        variant={variant}
        sx={{
          ...avatarSize,
          bgcolor: '#f5f5f5',
          color: '#999',
          border: '1px solid #e0e0e0',
          ...props.sx
        }}
        {...props}
      >
        {fallbackIcon && <ImageIcon sx={{ fontSize: size * 0.4 }} />}
      </Avatar>
    )
  }

  // Show loaded image
  return (
    <Avatar
      src={cachedSrc}
      alt={alt}
      variant={variant}
      sx={{
        ...avatarSize,
        transition: 'opacity 0.3s ease',
        ...props.sx
      }}
      {...props}
    />
  )
}

export default CachedImage